import type { ClinicSession } from "@auth/clinicAuthClient";

const STORAGE_KEY = "brian.session";

interface StoredSessions {
  clinic: ClinicSession | null;
  user: ClinicSession | null;
}

const EMPTY: StoredSessions = { clinic: null, user: null };

const isSession = (s: unknown): s is ClinicSession => {
  if (!s || typeof s !== "object") return false;
  const r = s as Record<string, unknown>;
  return (
    typeof r.accessToken === "string" &&
    typeof r.refreshToken === "string" &&
    typeof r.accessExpiresAt === "number" &&
    (r.scope === "clinic" || r.scope === "user") &&
    typeof r.clinicUuid === "string" &&
    (r.userUuid === null || typeof r.userUuid === "string")
  );
};

const isValid = (s: unknown): s is StoredSessions => {
  if (!s || typeof s !== "object") return false;
  const r = s as Record<string, unknown>;
  const clinicOk = r.clinic === null || isSession(r.clinic);
  const userOk = r.user === null || isSession(r.user);
  return clinicOk && userOk;
};

export const readSessions = (): StoredSessions => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    if (!isValid(parsed)) return EMPTY;
    if (!parsed.clinic) return EMPTY;
    return parsed;
  } catch {
    return EMPTY;
  }
};

export const writeSessions = (sessions: StoredSessions): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // ignore quota / disabled-storage errors
  }
};

export const clearSessions = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
